import { siteConfig } from '@/config/site.config'

export const contentI18nEn = (
  <div>
    <h2>Definitions</h2>
    <ul>
      <li>“<b>Company</b>”, “We”, “JBDA”, or “Seller”: refers to the legal entity providing the Service.</li>
      <li>“<b>Site</b>” or “Website”: refers to the current site through which the Subscriber establishes contact with the Company.</li>
      <li>“<b>Platform</b>”: denotes the platform provided by the Company for the Subscriber to access and use the Service offered thereon.</li>
      <li>“<b>General Terms of Sale</b>”, “GTS”, or “Terms”: refers to the document created by JBDA that aims to communicate to the Subscriber the legal and mandatory information related to the Service provided by the Company.</li>
      <li>“<b>Client</b>” or “<b>Subscriber</b>” (hereinafter referred to as “You”): refers to any individual who has used the Platform and/or has subscribed to the Service. This designation includes both professional and private clientele. When there is no need to distinguish between these types of clientele, the aforementioned designations shall prevail.</li>
      <li>“<b>Parties</b>”: refers to the collective designation including both the Company and the Subscriber.</li>
      <li>“<b>Service</b>”: refers to the service offered for sale by the Company through the Website. Specifically, JBDA provides a digital asset escrow service.</li>
      <li>“<b>Subscription</b>”: refers to the procedure allowing the Client to order the Service provided by the Company, which involves the payment of the Price by the Client.</li>
      <li>“<b>Special Terms of Sale</b>”, “STS”: refers to the service contract concluded between JBDA and the Client which is intended to govern the provision of the Service in a personalized manner. It is noteworthy that the Special Terms of Sale prevail over the General Terms of Sale.</li>
      <li>“<b>User</b>”: refers to any person visiting the Site.</li>
    </ul>
    <h2>Data Processing and Information Security</h2>
    <p>
      The purpose of these provisions is to inform the User and the Subscriber about the reasons why JBDA collects, processes, and protects their personal data. This policy, drafted by the Company, affirms and guarantees that the consumer, as defined under the Consumer Code, is aware of the following elements:
    </p>
    <ul>
      <li>The manner in which their personal data are collected and secured</li>
      <li>The type of data collected</li>
      <li>The use of the collected data</li>
      <li>The purpose of this collection</li>
      <li>The rights they hold concerning these data</li>
      <li>The Site’s cookie policy</li>
    </ul>
    <p>
      This policy supplements the <a href="./legal-mentions">legal notices</a> and the <a href="./cgu">general terms of sale of the Service</a> of our Site.
    </p>
    <h2>Applicable Laws</h2>
    <p>
      In accordance with the General Data Protection Regulation (GDPR), this privacy policy complies with the following regulations.
    </p>
    <p>
      Personal data must be:
    </p>
    <ul>
      <li>
        Processed lawfully, fairly, and transparently in relation to the data subject (lawfulness, fairness, and transparency).
      </li>
      <li>
        Collected for specified, explicit, and legitimate purposes and not further processed in a manner that is incompatible with those purposes; further processing for archiving in the public interest, scientific or historical research purposes or statistical purposes shall not be considered, in accordance with Article 89(1), to be incompatible with the initial purposes (purpose limitation).
      </li>
      <li>
        Adequate, relevant, and limited to what is necessary in relation to the purposes for which they are processed (data minimization).
      </li>
      <li>
        Accurate and, where necessary, kept up to date; every reasonable step must be taken to ensure that personal data that are inaccurate, with regard to the purposes for which they are processed, are erased or rectified without delay (accuracy).
      </li>
      <li>
        Kept in a form which permits identification of data subjects for no longer than is necessary for the purposes for which the personal data are processed; personal data may be stored for longer periods insofar as the personal data will be processed solely for archiving purposes in the public interest, scientific or historical research purposes or statistical purposes in accordance with Article 89(1) subject to implementation of the appropriate technical and organisational measures required by the regulation in order to safeguard the rights and freedoms of the data subject (storage limitation).
      </li>
      <li>
        Processed in a manner that ensures appropriate security of the personal data, including protection against unauthorized or unlawful processing and against accidental loss, destruction, or damage, using appropriate technical or organizational measures (integrity and confidentiality).
      </li>
    </ul>
    <p>
      Processing is lawful only if, and to the extent that, at least one of the following applies:
    </p>
    <ul>
      <li>
        The data subject has consented to processing of his or her personal data for one or more specific purposes.
      </li>
      <li>
        Processing is necessary for the performance of a contract to which the data subject is party or in order to take steps at the request of the data subject prior to entering into a contract.
      </li>
      <li>
        Processing is necessary for compliance with a legal obligation to which the controller is subject.
      </li>
      <li>
        Processing is necessary in order to protect the vital interests of the data subject or of another natural person.
      </li>
      <li>
        Processing is necessary for the performance of a task carried out in the public interest or in the exercise of official authority vested in the controller.
      </li>
      <li>
        Processing is necessary for the purposes of the legitimate interests pursued by the controller or by a third party, except where such interests are overridden by the fundamental rights and freedoms of the data subject which require protection of personal data, particularly where the data subject is a child.
      </li>
    </ul>
    <h2>Consent</h2>
    <p>
      Users agree that by using our Site, they consent to:
    </p>
    <ul>
      <li>The terms set out in this privacy policy</li>
      <li>The collection, use, and storage of data listed in this policy</li>
    </ul>
    <h2>Collection and Security of Personal Data</h2>
    <p>
      The Company implements appropriate organizational and technical measures to safeguard your personal data from malicious intrusion, loss, alteration, or unauthorized disclosure. These measures are designed to maintain the security and confidentiality of your personal data and provide a level of security commensurate with the risk. Given the inherent challenges of conducting activities on the internet and the risks, of which you are duly informed, resulting from the transmission of data electronically, the Company cannot be held to an outcome obligation. In accordance with EU Regulation 2016/679 on the protection of personal data (GDPR), JBDA assures the User and the Subscriber that the personal data collected are processed in compliance with the GDPR.
    </p>
    <p>
      JBDA only collects and uses the personal data necessary (i) for the proper execution of the Subscription process and (ii) for the correct performance of the Service.
    </p>
    <h3>Types of Personal Data Collected</h3>
    <p>
      We may implement processing of your personal data, which includes the following:
    </p>
    <ul>
      <li>Name</li>
      <li>First names</li>
      <li>Email address</li>
      <li>Postal address</li>
      <li>Nationality</li>
      <li>Occupation</li>
      <li>Sources of income</li>
    </ul>
    <p>
      Please note that the list mentioned above is non-exhaustive and may be subject to successive updates, particularly for industrial, commercial, or regulatory reasons.
    </p>
    <p>
      These data may be collected during the following actions:
    </p>
    <ul>
      <li>Subscription to the Service</li>
      <li>Signing up for our newsletter</li>
      <li>Scheduling an appointment or requesting contact</li>
    </ul>
    <p>
      Please be aware that we only collect data that helps us achieve the objectives stated in this privacy policy. We will not collect additional data without informing you in advance.
    </p>
    <h2>AI Chatbot (Conversational Assistant)</h2>
    <p>
      We provide a conversational assistant (“Chatbot”) powered by ChatGPT technology from OpenAI, L.L.C., 3180 18th Street, San Francisco, CA (USA). When you interact with the Chatbot, the messages you type may contain personal data.
    </p>
    <h3>Data processed</h3>
    <ul>
      <li>Content of your messages sent to the Chatbot</li>
      <li>Technical metadata (timestamp, session identifier, truncated IP address)</li>
    </ul>
    <h3>Purposes and legal basis</h3>
    <p>
      Processing is necessary for our legitimate interest (Article 6‑1‑f GDPR) in answering visitors’ questions and improving our support. No automated decision producing legal effects is taken based on the Chatbot’s replies.
    </p>
    <h3>Recipients and transfers outside the EU/EEA</h3>
    <p>
      The data are processed by OpenAI in the United States. The transfer is governed by the European Commission’s Standard Contractual Clauses and OpenAI’s participation in the EU‑US Data Privacy Framework.
    </p>
    <h3>Retention period</h3>
    <p>
      JBDA keeps no logs of the conversations. OpenAI retains inputs and outputs for up to 30 days solely for abuse detection, after which they are automatically deleted.
    </p>
    <h3>Transparency – AI Act</h3>
    <p>
      The Chatbot’s responses are automatically generated by artificial intelligence; please verify them before relying on them. We encourage you not to share sensitive information (health data, credit‑card numbers, etc.).
    </p>    
    <h2>The Purpose of This Collection</h2>
    <p>
      In your interactions with the Company, it may collect and process Personal Data concerning you for managing activities. In this context, the Company applies the principles defined by legal and regulatory provisions concerning the protection of Personal Data, particularly those outlined in Regulation (EU) 2016/679 of 27 April 2016 on the protection of natural persons with regard to the processing of Personal Data (“GDPR”), as well as Law No. 78-17 of 6 January 1978 relating to information technology, files, and freedoms (known as the “Data Protection Act”) and its implementing decrees. Moreover, the collection and processing of the following data is solely for the purpose of effectively providing the services you have requested.
    </p>
    <p>
      Furthermore, JBDA guarantees its best efforts in securing your personal information. However, it cannot ensure that unauthorized third parties will not breach our security system and use your personal information for purposes not in line with the intended processing. Thus, you agree to provide your personal information with full awareness.
    </p>
    <p>
      The secondary purposes of data collection include:
    </p>
    <ul>
      <li>Communication to partners of the data essential for the provision of the Service, which will be carried out in compliance with the provisions of this article</li>
      <li>Management, processing, and tracking of information requests and exchanges with Subscribers</li>
      <li>Management of our relations with our Clients (pre-contractual, contractual, and post-contractual relations)</li>
      <li>Management of our relationships with our partners and service providers</li>
      <li>Management by the Company of requests to exercise the rights of the data subjects concerning the protection of personal data, in accordance with its legal and regulatory obligations</li>
    </ul>
    <p>
      Moreover, data collection is limited in time to a period of two (2) years from the date of data collection.
    </p>
    <h2>Sharing of Personal Data</h2>
    <h3>Employees</h3>
    <p>
      We may disclose to any member of our organization the user data they reasonably need to achieve the objectives set out in this policy.
    </p>
    <h3>Digital Asset Service Provider Partner</h3>
    <p>
      We may share the collected data with our Digital Asset Service Provider partner for the proper execution of the Service.
    </p>
    <h3>Other Disclosures</h3>
    <p>
      We commit to not selling or sharing your data with other third parties, except in the following cases:
    </p>
    <ul>
      <li>If required by law</li>
      <li>If necessary for any legal proceeding</li>
      <li>To prove or protect our legal rights</li>
      <li>To buyers or prospective buyers of this company in the event we seek to sell the company</li>
    </ul>
    <p>
      If you follow hyperlinks from our Site to another site, please be aware that we are not responsible for and do not have control over their privacy practices and policies.
    </p>
    <h2>Your Rights as a User</h2>
    <p>
      Under the GDPR, users have the following rights as data subjects:
    </p>
    <ul>
      <li>Right of access</li>
      <li>Right to rectification</li>
      <li>Right to erasure</li>
      <li>Right to restrict processing</li>
      <li>Right to data portability</li>
      <li>Right to object</li>
    </ul>
    <p>
      You can find more information about these rights in Chapter 3 (Articles 12-23) of the GDPR.
    </p>
    <h2>How to Modify, Delete, or Contest Collected Data</h2>
    <p>
      If you wish to have your information deleted or modified in any way, you can contact our privacy protection service by email at: <a href={`mailto:${siteConfig.mail.privacyAddress}`}>{siteConfig.mail.privacyAddress}</a>.
    </p>
    <h2>Cookie Policy</h2>
    <p>
      Our website uses cookies and similar technologies to enhance your user experience, provide certain functionalities, and analyze traffic. Traffic analysis begins only with your consent and stops upon request. These tools help us understand how visitors interact with our website, thereby allowing us to improve our services.
    </p>
    <p>
      If you choose not to give your consent, or if you withdraw your consent at any point, your user experience on our Site will not be diminished, but we will not be able to finely capture your preferences and the effectiveness of our Site and certain functionalities will not be accessible (appointment scheduling, newsletter subscription).
    </p>
    <p>
      You have the ability to manage your cookie preferences at any time via your browser settings and the cookie link present on each footer of the page.
    </p>
    <h2>Changes</h2>
    <p>
      This privacy policy may be amended occasionally to maintain compliance with the law and to reflect any changes to our data collection process. We recommend that our users periodically review our policy to ensure they are informed of any updates. If necessary, we may inform users by email of changes to this policy.
    </p>
    <h2>Contact</h2>
    <p>
      If you have any questions, please do not hesitate to contact us using the following:
    </p>
    <ul>
      <li>Phone number: {siteConfig.phone}</li>
      <li>Email address: <a href={`mailto:${siteConfig.mail.contactAddress}`}>{siteConfig.mail.contactAddress}</a></li>
      <li>Address: JBDA - Bat. 3, 535 Route des Lucioles, 06560 Sophia Antipolis</li>
    </ul>
  </div>
)
